import {
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';
import { useHistory } from 'react-router';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { referenceRouteConfig } from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { useQueryParam } from 'src/hooks/useQueryParam';
import Config from 'src/lib/config';

import { PublishModeTab, PublishYourSchema } from './LegacyPublishYourSchema';

interface Props {
  /**
   * passed in graph id if not on a graph route
   */
  forGraphId?: string;
  isOpen: boolean;
  /**
   * User interacted in some way to close the modal
   */
  onClose: () => void;
  __test?: {
    initialTab?: PublishModeTab;
    staging?: boolean;
  };
}

export const PublishYourSchemaModal = ({
  forGraphId,
  isOpen,
  onClose,
  __test,
}: Props) => {
  const {
    graphId: graphIdFromRouteParams,
    graphVariant: graphVariantFromRouteParams,
  } = useGraphRef() ?? {};
  const graphIdFromQueryParams = useQueryParam('graphId');
  const graphVariantFromQueryParams = useQueryParam('variant');
  const graphId =
    graphIdFromRouteParams ?? graphIdFromQueryParams ?? forGraphId;
  const graphVariant =
    graphVariantFromRouteParams ??
    graphVariantFromQueryParams ??
    Config.service.defaultVariant;
  const history = useHistory();
  const firstPublish = !graphIdFromRouteParams;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          onClose();
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {firstPublish ? 'Publish your schema' : 'Update schema'}
            </ModalTitle>
            {firstPublish && (
              <ModalDescription>
                Pick one of these options. You can always change it later.
              </ModalDescription>
            )}
          </ModalHeader>
          <ModalBody>
            {graphId ? (
              <PublishYourSchema
                firstPublish={firstPublish}
                graphId={graphId}
                graphVariant={graphVariant}
                onComplete={() =>
                  history.push(
                    referenceRouteConfig.path({ graphId, graphVariant }),
                  )
                }
                onClose={onClose}
                __test={__test}
              />
            ) : (
              <NotFound
                title="Unexpected Error"
                subtitle={
                  <>
                    We've sent you somewhere we shouldn't have! Please{' '}
                    <ContactSupportLink>contact support</ContactSupportLink> and
                    let us know
                  </>
                }
              />
            )}
          </ModalBody>
        </ModalContent>
      </Form>
    </Modal>
  );
};
